import { Box, Group, Text, Stack, Tooltip } from '@mantine/core';
import { plants, MONTH_NAMES, LAST_FROST_WEEK, FIRST_FROST_WEEK } from '../data/plants';

const BAR_H = 20;
const LABEL_W = 140;
const TOTAL_WEEKS = 52;
const CELL_W = 16;

const SPACE_COLOR = '#dee2e6';
const HARVEST_COLOR = '#e8590c';

function weekToDate(week: number): string {
  const d = new Date(2026, 0, 1);
  d.setDate(d.getDate() + (week - 1) * 7);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface OccupancyInfo {
  name: string;
  spaceStart: number;
  spaceEnd: number;
  harvestStart: number;
  harvestEnd: number;
}

function getOccupancy(isSelected: (name: string) => boolean): OccupancyInfo[] {
  return plants
    .filter(p => isSelected(p.name))
    .filter(p => p.category !== 'herb' && p.category !== 'flower')
    .map((p) => {
      let spaceStart = 52;
      if (p.transplant) spaceStart = Math.min(spaceStart, p.transplant[0]);
      if (p.directSow) spaceStart = Math.min(spaceStart, p.directSow[0]);
      if (spaceStart === 52 && p.indoorStart) spaceStart = p.indoorStart[0];

      return {
        name: p.name,
        spaceStart,
        spaceEnd: p.harvestEnd,
        harvestStart: p.harvestStart,
        harvestEnd: p.harvestEnd,
      };
    })
    .sort((a, b) => a.spaceStart - b.spaceStart);
}

interface HarvestChartProps {
  isSelected: (name: string) => boolean;
}

export function HarvestChart({ isSelected }: HarvestChartProps) {
  const occupancy = getOccupancy(isSelected);

  const monthPositions = MONTH_NAMES.map((name, i) => {
    const weeksPerMonth = [4, 4, 5, 4, 5, 4, 4, 5, 4, 4, 5, 4];
    let startWeek = 1;
    for (let j = 0; j < i; j++) startWeek += weeksPerMonth[j];
    return { name, start: startWeek, weeks: weeksPerMonth[i] };
  });

  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">
        Shows how long each plant occupies garden space (gray) and when you can harvest (orange).
        Helps plan succession planting and bed rotation.
      </Text>

      <Group gap="lg" mb="xs">
        <Group gap={4}>
          <Box style={{ width: 14, height: 14, backgroundColor: SPACE_COLOR, borderRadius: 3 }} />
          <Text size="xs">Occupying space</Text>
        </Group>
        <Group gap={4}>
          <Box style={{ width: 14, height: 14, backgroundColor: HARVEST_COLOR, borderRadius: 3 }} />
          <Text size="xs">Harvest window</Text>
        </Group>
      </Group>

      <Box style={{ overflowX: 'auto' }}>
        <Group gap={0} wrap="nowrap" mb={4}>
          <Box style={{ width: LABEL_W, minWidth: LABEL_W }} />
          {monthPositions.map(({ name, weeks }) => (
            <Text
              key={name}
              size="xs"
              fw={600}
              c="dimmed"
              style={{ width: weeks * CELL_W, textAlign: 'center' }}
            >
              {name}
            </Text>
          ))}
        </Group>

        <Box style={{ position: 'relative' }}>
          <Box
            style={{
              position: 'absolute',
              left: LABEL_W + (LAST_FROST_WEEK - 1) * CELL_W,
              top: 0, bottom: 0,
              borderLeft: '2px dashed var(--mantine-color-red-6)',
              zIndex: 10, pointerEvents: 'none',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              left: LABEL_W + (FIRST_FROST_WEEK - 1) * CELL_W,
              top: 0, bottom: 0,
              borderLeft: '2px dashed var(--mantine-color-red-6)',
              zIndex: 10, pointerEvents: 'none',
            }}
          />

          {occupancy.map(({ name, spaceStart, spaceEnd, harvestStart, harvestEnd }) => (
            <Group key={name} gap={0} wrap="nowrap" style={{ height: BAR_H + 4 }}>
              <Text
                size="xs"
                fw={500}
                style={{
                  width: LABEL_W, minWidth: LABEL_W,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  paddingRight: 8,
                }}
              >
                {name}
              </Text>
              <Box style={{ position: 'relative', width: TOTAL_WEEKS * CELL_W, height: BAR_H }}>
                <Tooltip label={`In garden: ${weekToDate(spaceStart)} – ${weekToDate(spaceEnd)}`} withArrow>
                  <Box
                    style={{
                      position: 'absolute',
                      left: (spaceStart - 1) * CELL_W,
                      width: (spaceEnd - spaceStart + 1) * CELL_W,
                      height: BAR_H - 4,
                      top: 2,
                      backgroundColor: SPACE_COLOR,
                      borderRadius: 3,
                    }}
                  />
                </Tooltip>
                <Tooltip label={`Harvest: ${weekToDate(harvestStart)} – ${weekToDate(harvestEnd)}`} withArrow>
                  <Box
                    style={{
                      position: 'absolute',
                      left: (harvestStart - 1) * CELL_W,
                      width: (harvestEnd - harvestStart + 1) * CELL_W,
                      height: BAR_H - 4,
                      top: 2,
                      backgroundColor: HARVEST_COLOR,
                      borderRadius: 3,
                      opacity: 0.85,
                    }}
                  />
                </Tooltip>
              </Box>
            </Group>
          ))}
        </Box>
      </Box>
    </Stack>
  );
}
